import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import "./sops.css";

const extractRows = (response) => {
  const payload = response?.data?.data;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;

  return [];
};

const renderHtml = (content) => ({ __html: content || "<p>No SOP selected.</p>" });

export default function SOPViewer() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sops, setSops] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const filteredSops = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return sops;

    return sops.filter((row) =>
      [row.title, row.sop_for].some((value) => String(value || "").toLowerCase().includes(keyword))
    );
  }, [search, sops]);

  const selectedSop = useMemo(
    () => filteredSops.find((row) => String(row.id) === String(selectedId)) || filteredSops[0] || null,
    [filteredSops, selectedId]
  );

  const loadSops = useCallback(async () => {
    if (!branch_id) {
      setSops([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await UserServices.getSops({
        branch_id,
        status: "1",
        limit: 100,
        page: 1,
      });
      const rows = extractRows(response);
      setSops(rows);
      setSelectedId(rows[0]?.id || null);
    } catch (error) {
      setSops([]);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  }, [branch_id]);

  useEffect(() => {
    loadSops();
  }, [loadSops]);

  useEffect(() => {
    if (selectedSop) {
      setSelectedId(selectedSop.id);
    }
  }, [selectedSop]);

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="sop-shell">
            <section className="sop-hero">
              <div>
                <span className="sop-eyebrow">SOPs</span>
                <h2>Standard Operating Procedures</h2>
                <p>
                  Review the latest SOPs shared by your Fitness Manager in a clean, readable
                  format.
                </p>
              </div>
              <div className="sop-hero-chip">Trainer View</div>
            </section>

            <section className="sop-grid trainer">
              <div className="sop-card">
                <div className="sop-card-header sop-card-header-spread">
                  <div>
                    <span className="sop-section-label">Available SOPs</span>
                    <h4>SOP Library</h4>
                  </div>
                  <input
                    className="form-control"
                    placeholder="Search SOPs"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>

                {loading ? (
                  <div className="sop-empty">Loading SOPs...</div>
                ) : filteredSops.length ? (
                  <div className="sop-viewer-list">
                    {filteredSops.map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        className={`sop-viewer-item ${String(selectedSop?.id) === String(row.id) ? "active" : ""}`}
                        onClick={() => setSelectedId(row.id)}
                      >
                        <strong>{row.title}</strong>
                        <span>{row.sop_for}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="sop-empty">No SOPs are available for your branch yet.</div>
                )}
              </div>

              <div className="sop-card">
                {selectedSop ? (
                  <>
                    <div className="sop-card-header">
                      <div>
                        <span className="sop-section-label">{selectedSop.sop_for}</span>
                        <h4>{selectedSop.title}</h4>
                        <p>
                          Updated SOP document shared for your team. Tables, lists, and formatted
                          instructions are shown exactly as saved.
                        </p>
                      </div>
                    </div>
                    <div
                      className="sop-content-preview sop-content-viewer"
                      dangerouslySetInnerHTML={renderHtml(selectedSop.content)}
                    />
                  </>
                ) : (
                  <div className="sop-empty">Select an SOP from the left to read it here.</div>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
